import React, { useState, useEffect, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppShell,
  ToastContainer,
  GlobalSearch,
  StatusBanner,
  useAuth,
} from "@nuance/ui";
import { ProductSwitcher } from "./ProductSwitcher";
import { AiChatWidget } from "../components/shared/AiChatWidget";
import { ErrorBoundary } from "../components/shared/ErrorBoundary";
import { UserSettingsModal } from "../components/shared/UserSettingsModal";
import { AccessEndsBanner } from "../components/shared/AccessEndsBanner";
import { BannerProvider, useBanner } from "../contexts/BannerContext";
import {
  CurrentUserProvider,
  permissionsUnconfirmed,
  useCurrentUser,
} from "../contexts/CurrentUserContext";
import { ProjectProvider } from "../contexts/ProjectContext";
import { ProjectSelector } from "../components/shared/ProjectSelector";
import { ToastProvider, useToastContext } from "../contexts/ToastContext";
import { useNavBadges } from "../hooks/useNavBadges";
import type { ProductConfig } from "./productConfig";

interface ProductLayoutProps {
  config: ProductConfig;
}

// Nav items restricted to specific emails
const RESTRICTED_NAV: Record<string, Set<string>> = {
  "admin-access": new Set(["brennan@nuance.team", "mark@nuance.team"]),
};

function ProductLayoutInner({ config }: ProductLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { toasts, dismissToast } = useToastContext();
  const { currentUser, error: meError } = useCurrentUser();
  const [searchOpen, setSearchOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { banner, dismiss } = useBanner();
  const navBadges = useNavBadges();

  // Filter nav items — hide restricted items unless the user's email is in the allowlist
  const filteredNavItems = useMemo(() => {
    const email = currentUser?.email?.toLowerCase();
    return config.navItems.filter((item) => {
      const allowed = RESTRICTED_NAV[item.id];
      if (!allowed) return true; // no restriction
      return email ? allowed.has(email) : false;
    });
  }, [config.navItems, currentUser]);

  // Resolve current page from pathname, handling parameterized routes like /clients/:id
  const currentPage = (() => {
    const entries = Object.entries(config.pageRouteMap);
    // Try exact match first
    for (const [pageId, route] of entries) {
      if (route === location.pathname) return pageId;
    }
    // Try parameterized match (e.g. /elements/clients/:id → client-detail)
    for (const [pageId, route] of entries) {
      if (!route.includes(":")) continue;
      const base = route.replace(/\/:[^/]+/g, "");
      if (location.pathname.startsWith(base + "/")) return pageId;
    }
    return Object.keys(config.pageRouteMap)[0] ?? "";
  })();

  // Ctrl+K / Cmd+K to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleNavigate = (pageId: string) => {
    const route = config.pageRouteMap[pageId];
    if (route) navigate(route);
  };

  return (
    <>
      <AppShell
        appName={config.appName}
        appSubtitle={config.appSubtitle}
        navItems={filteredNavItems}
        currentPage={currentPage.replace(/-detail$/, "")}
        onNavigate={handleNavigate}
        userName={user?.name ?? user?.email ?? "User"}
        userInitials={
          user?.name
            ?.split(" ")
            .map((n) => n[0])
            .join("") ??
          user?.email?.[0]?.toUpperCase() ??
          "U"
        }
        userEmail={user?.email}
        userRole={
          user?.roles.includes("admin")
            ? "Administrator"
            : user?.roles.includes("client")
              ? "Client User"
              : "User"
        }
        onLogout={logout}
        onSearch={() => setSearchOpen(true)}
        breadcrumbs={config.getBreadcrumbs(currentPage)}
        logoIcon={config.logoIcon}
        logoGradient={config.logoGradient}
        onUserClick={() => setSettingsOpen(true)}
        navBadges={navBadges}
        headerActions={
          <div className="flex items-center gap-3">
            <ProjectSelector />
            <ProductSwitcher currentProduct={config.id} />
          </div>
        }
        banner={
          banner.active || meError || currentUser?.access_notices?.length ? (
            <>
              <AccessEndsBanner notices={currentUser?.access_notices} />
              {/* not dismissible: admin-only controls stay locked until a reload works */}
              {meError && (
                <StatusBanner
                  severity="degraded"
                  message={permissionsUnconfirmed(meError)}
                />
              )}
              {banner.active && (
                <StatusBanner
                  severity={banner.severity}
                  message={banner.message}
                  onDismiss={dismiss}
                />
              )}
            </>
          ) : undefined
        }
      >
        <Outlet />
      </AppShell>
      <AiChatWidget context={{ page: currentPage }} />
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={handleNavigate}
        items={config.searchItems}
        categories={config.searchCategories}
      />
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <UserSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </>
  );
}

export function ProductLayout({ config }: ProductLayoutProps) {
  return (
    <ErrorBoundary>
      <CurrentUserProvider>
        <ProjectProvider>
          <BannerProvider>
            <ToastProvider>
              <ProductLayoutInner config={config} />
            </ToastProvider>
          </BannerProvider>
        </ProjectProvider>
      </CurrentUserProvider>
    </ErrorBoundary>
  );
}
